import type { ChatbotLinkHandler } from "../types";

/**
 * Scheme-based handler for `indigo://appointment-doc?…` links emitted
 * by the medical-records agent tool. Canonical URL carries
 * `uploaded_file_id=<uuid>&page=<N>`, but the parser is lenient so the
 * link stays clickable when the LLM paraphrases the query shape.
 * Anything that carries a scheme prefix + a positive page + EITHER an
 * id-shaped value OR a filename is acceptable. The downstream subscriber
 * (`AppointmentDetails`) resolves a filename to an uploaded-file id by
 * looking it up in the appointment's file list.
 */
const APPOINTMENT_DOC_SCHEME_PREFIX = "indigo://appointment-doc";

// Keys the LLM has been observed to emit for the identifier and for the
// filename. We accept any of them; canonical is `uploaded_file_id`.
const ID_KEYS = ["uploaded_file_id", "file_id", "uploadedFileId", "fileId"];
const FILENAME_KEYS = ["file", "filename", "fileName", "name"];

// Loose UUID-ish check — accepts dashed UUIDs and the `[0-9a-f]{24}`
// style ObjectIds we occasionally mint. Used only to disambiguate when a
// caller emits `id=...` (which could be either a uuid or a filename).
const UUID_LIKE_RE = /^[0-9a-f][0-9a-f-]{7,}$/i;

type ParsedAppointmentDoc = {
  uploadedFileId?: string;
  filename?: string;
  page: number;
};

export function parseAppointmentDocHref(
  href: string,
): ParsedAppointmentDoc | null {
  if (!href.toLowerCase().startsWith(APPOINTMENT_DOC_SCHEME_PREFIX)) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  // Page — accept `page`, `p`, or a trailing numeric path segment so
  // path-style variants (`/page/7`) also work.
  let page: number | null = null;
  for (const raw of [
    url.searchParams.get("page"),
    url.searchParams.get("p"),
    ...url.pathname.split("/").reverse(),
  ]) {
    if (!raw) continue;
    const n = Number(raw);
    if (Number.isInteger(n) && n > 0) {
      page = n;
      break;
    }
  }
  if (page === null) return null;

  let uploadedFileId: string | undefined;
  for (const key of ID_KEYS) {
    const v = url.searchParams.get(key);
    if (v) {
      uploadedFileId = v;
      break;
    }
  }

  let filename: string | undefined;
  for (const key of FILENAME_KEYS) {
    const v = url.searchParams.get(key);
    if (v) {
      filename = v;
      break;
    }
  }

  // Bare `id=...` — treat as id when it looks uuid-ish, else filename.
  if (!uploadedFileId && !filename) {
    const bareId = url.searchParams.get("id");
    if (bareId) {
      if (UUID_LIKE_RE.test(bareId)) {
        uploadedFileId = bareId;
      } else {
        filename = bareId;
      }
    }
  }

  if (!uploadedFileId && !filename) return null;
  const parsed: ParsedAppointmentDoc = { page };
  if (uploadedFileId) parsed.uploadedFileId = uploadedFileId;
  if (filename) parsed.filename = filename;
  return parsed;
}

/**
 * Handler for `indigo://appointment-doc?…` links. Parses leniently and
 * forwards to `ctx.onRequestAppointmentDocument`, which is expected to
 * open the Case Documents sidebar's inline PDF viewer. When the URL
 * carries only a filename (LLM-paraphrased form), the downstream
 * subscriber resolves it to an uploaded-file id; when it carries only
 * an id, the filename is omitted and the subscriber uses the id
 * directly.
 */
export const appointmentDocumentHandler: ChatbotLinkHandler = {
  id: "appointment-document",
  matches: (href) =>
    href.toLowerCase().startsWith(APPOINTMENT_DOC_SCHEME_PREFIX),
  onClick: (href, ctx) => {
    const parsed = parseAppointmentDocHref(href);
    if (!parsed) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[chatbotLinks] Malformed appointment-document link, ignoring click:",
          href,
        );
      }
      return;
    }
    if (!ctx.onRequestAppointmentDocument) {
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          "[chatbotLinks] Appointment-document link clicked but no onRequestAppointmentDocument handler is wired:",
          href,
        );
      }
      return;
    }
    ctx.onRequestAppointmentDocument(parsed);
  },
};
